import * as React from 'react';
import styled from '@emotion/styled';
import trsSetupComplete from '../../images/manual/completed-assembly.png';
import trsSetupOne from '../../images/manual/step-1.png';
import trsSetupTwo from '../../images/manual/step-2.png';
import trsSetupThree from '../../images/manual/step-3.png';
import trsSetupFour from '../../images/manual/step-4.png';
import trsSetupFive from '../../images/manual/step-5.png';
import trsSetupSix from '../../images/manual/step-6.png';
import trsSetupSeven from '../../images/manual/step-7.png';

const ManualContent = styled('div')`
  flex-grow: 1;
  margin: 0;
  padding: 2rem 3rem;
  background: white;

  @media (max-width: 1024px) {
    padding: 1rem;

    img {
      max-width: 100%;
    }
  }
`;

class Manual extends React.PureComponent<{}, {}> {
  render () {
    return (
      <ManualContent>
        <h1>Total Reaction System &mdash; Manual & Quick Start Guide</h1>
        <h2>Table of Contents</h2>
        <ol>
          <li>Introduction</li>
          <li>Contents</li>
          <li>Assembly Instructions</li>
          <li>Completed Assembly</li>
          <li>Step 1: Unpacking</li>
          <li>Step 2: Crossbar Assembly</li>
          <li>Step 3: Measurement</li>
          <li>Step 4: Base and Stand Assembly</li>
          <li>Step 5: Height Adjustment</li>
          <li>Step 6: Crossbar Mount</li>
          <li>Step 7: Screen Attachment</li>
          <li>Screen Care</li>
          <li>Screen Creasing</li>
          <li>Screen Protection</li>
          <li>Cleaning</li>
          <li>Marks</li>
          <li>Distributors</li>
          <li>Australia</li>
          <li>United Kingdom & Europe</li>
          <li>Warranty Information – Australia Only</li>
          <li>Legal Information</li>
        </ol>
         
        <h2>Introduction</h2>
        <p>
          The Total Reaction Screen (TRS) is a training and coaching aid invented and developed by Australian Sporting Innovations. The TRS has been designed to improve reaction and response times for sporting participants of all ages, and for the training and coaching of children, novices and professionals alike.<br/>
          <br/>
          The TRS may be applied in a variety of sports where response and reaction are an essential criterion of achieving high performance and good skills.<br/>
          <br/>
          This manual contains contents, assembly instructions, care, warranty and distribution information. Explanations of how the TRS works and its benefits can be found on the website as can a sample set of training drills. To obtain the full benefits of using the TRS, please take the time to read this Manual prior to use. Continued use of the TRS will give you many hours of enjoyment and satisfaction with your personal improvement.
        </p>

        <h2>Contents</h2>
        <p>The TRS package consists of the following contents:</p>
        <ul>
          <li>2 x Bases</li>
          <li>2 x Lower section up rights</li>
          <li>2 x Upper section up rights with height adjustment holes</li>
          <li>2 x Interlocking crossbar sections</li>
          <li>6 x Thumb wheel locking bolts (shorter bolts are for lower section connections)</li>
          <li>1 x Crossbar Locking Cup Head Bolt & Nut</li>
          <li>1 x Crossbar locking ‘O’ Ring Bolt & Nut (for alternative suspended storage)</li>
          <li>2 x Screen sections</li>
        </ul>
        
        <h2>Assembly Instructions</h2>
        <p>
          The difference between winning and losing could be a fraction of a second of reaction time – don’t leave it to chance. Practice with the TRS!
        </p>
        <ul>
          <li>Please follow the assembly instructions in order of numbers as set out in this manual.</li>
          <li>Best assembled in a large open space.</li>
          <li>Check the contents to ensure none are missing.</li>
          <li>No tools are required for assembly.</li>
        </ul>
        <p>
          For more information in the UK and Europe, contact SAQ International on +44 (0) 1664 810 101 or email info@saqinternational.com.
        </p>

        <h3>Completed Assembly</h3>
        <img src={trsSetupComplete} />

        <h3>Step 1: Unpacking</h3>
        <img src={trsSetupOne} />
        <p>Unpack all frame pieces.</p>

        <h3>Step 2: Crossbar Assembly</h3>
        <img src={trsSetupTwo} />
        <p>Slide crossbar pieces together and tighten ‘O’ ring bolt and nut.</p>

        <h3>Step 3: Measurement</h3>
        <img src={trsSetupThree} />
        <p>Place both bases at distance apart equal to cross-bar length.</p>

        <h3>Step 4: Base and Stand Assembly</h3>
        <img src={trsSetupFour} />
        <p>Insert lower sections upright on to bases, insert shorter locking thumb wheels and tighten.</p>   

        <h3>Step 5: Height Adjustment</h3>
        <img src={trsSetupFive} />
        <p>Place crossbar connecting collar on to upper section upright at desired height setting, insert locking thumb wheels and tighten.</p>

        <h3>Step 6: Crossbar Mount</h3>
        <img src={trsSetupSix} />
        <p>Lift crossbar and upright sections and lower into lower section uprights.</p>

        <h3>Step 7: Screen Attachment</h3>
        <img src={trsSetupSeven} />
        <p>
          Slide screens into tracks on crossbar and line up to create screen.<br/>
          <br/>
          <strong>Important Note:</strong> Screens are marked front and rear to ensure correct locations for screens. Screens are inserted shiny side facing outwards both front and rear.
        </p>
         
        <h2>Screen Care</h2>
        <p>
          The high-quality vinyl material used in the TRS is UV protected and has been chosen for its durability and longevity. The screen life can be prolonged by following a few simple steps. The key to screen protection and availability for use is to avoid creasing which generally arises through improper storage or from placing weight on top of the wrapped-up screens during storage.<br/>
          <br/>
          The preferred method of storage of the screens when not in use is to lightly wrap the screens around the provided storage cylinder. This is the way the screens will be delivered at time of purchase. To use, the screens are unwound so that they hang free. The screens are then simply slid into the sail track connection and are ready for use.<br/>
          <br/>
          When you have finished using the screens and wish to breakdown the frame and store the TRS away before its next use, it is essential that the storage of the screens be properly considered.<br/>
          <br/> 
          Remove the screens from the sail track, folding each screen double at the flexible midpoint of the upper screen webbing. Both screens are then rolled onto the storage cylinder and placed on top of the frame in the carry bag. Time taken in using the proper storage technique will avoid creasing of the screen material.
        </p>


        <h3>Screen Creasing</h3>
        <p>
          Depending on the length of time and the manner in which the screens have been stored, some creasing of screens may occur from time to time.<br />
          <br />
          If creasing occurs, the creases may be removed by using a hair dryer, hanging the screens in a steamy roomy or by laying the screens flat on the floor and placing flat weights on the screens overnight.
        </p>

        <h3>Screen Protection</h3>
        <p>
          Despite the quality of the material used, wind, rain and sunlight will eventually combine to shorten screen life. If the TRS is being used outdoors, it is recommended that the TRS be dismantled and properly stored after use to achieve maximum screen life.<br />
          <br />
          When using the TRS indoors the frame can be left assembled with the screens in place and available for ready use.
        </p>

        <h3>Cleaning</h3>
        <p>
          Dirt, grease marks, etc. may be removed by lightly rubbing with warm soapy water. Excess water should be wiped off after cleaning.
        </p>
          
        <h3>Marks</h3>
        <p>
          In some training drills it may be appropriate to place “target marks” on individual or various screen sections.<br />
          <br />
          It is recommended that this be done by attaching different colours of electrical tape to create the target areas rather than marking the screens. The tape should be removed after use and any residual “sticky” areas cleaned off the screens.
        </p>

        <h2>Distributors</h2>
        <h3>Australia</h3>
        <p>
          Reaction Advantage Pty Ltd<br/>
          ABN 94 620 030 855<br/>
          23 Manuel Street, Mackay<br/>
          Queensland,<br/>
          4740<br/>
          Australia<br/>
          <br/>
          Contact via Phone:<br/>
          Australia: 0401 274 099<br/>
          International: +61 401 274 099<br/>
          Email: sales@totalreaction.com.au<br/>
          Web: www.totalreaction.com.au<br/>
        </p>

        <h3>United Kingdom &amp; Europe</h3>
        <p>
          SAQ® International<br/>
          Unit B, Asfordby Business Park,<br/>
          Melton Mowbray,<br/>
          Leicestershire<br/>
          LE14 3JL.<br/>
          United Kingdom<br/>
          <br/>	
          Contact via Phone:<br/>
          UK: 01 664 810 101<br/>
          International: +44 1664 810 101<br/>
          Email: info@saqinternational.com<br/>
          Web: www.saqinternational.com<br/>
        </p>
        
        <h2>Warranty Information – Australia Only</h2>
        <p>
          Our products come with a guarantee or warranty from the manufacturer. In addition, they come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled to a replacement or refund for a major failure and compensation for any other reasonably foreseeable loss or damage.<br/>
          <br/>
          You are entitled to have the goods repaired or replaced if the goods fail to be of acceptable quality and the failure does not amount to a major failure. Full details of your consumer rights may be found at www.consumerlaw.gov.au.<br/>
          <br/>
          Please send all warranty and support inquiries through our ticketing system via the email address sales@totalreaction.com.au.
        </p>

        <h2>Legal Information</h2>
        <p>
          Australian Sporting Innovations Pty Ltd (the Company) holds registered patents internationally in respect of the TRS. The Company and its authorised representatives reserve all rights in relation to the contents of this document and any plans and drawings associated therewith.<br/>
          <br/>
          No part of this document may be reproduced or transmitted in any form or by any means, electronic or mechanical, including photographing, photocopying, recording or by any information storage and retrieval system, without the prior written permission of the Company.<br/>
          <br/> 
          Copyright © Australian Sporting Innovations Pty Ltd 2019.<br/>
          <br/>
          The Company confirms that it has appointed Reaction Advantage Pty Ltd as its sole agent for the manufacture, sales and distribution of the TRS. The contact details for this company are available in the Distributors section of this manual.
        </p>
      </ManualContent>
    )
  }
}

export default Manual;
